import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { api } from '@/app/api';

const initialState = {
  items: [],
  byId: {},
  status: 'idle',
  error: null,
  saveStatus: 'idle',
  saveError: null,
};

function errMsg(err, fallback) {
  return err.response?.data?.message ?? fallback;
}

export const fetchBranches = createAsyncThunk(
  'branches/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/owner/branches');
      return data.branches ?? [];
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось загрузить филиалы'));
    }
  },
);

export const fetchBranch = createAsyncThunk(
  'branches/fetchOne',
  async (branchId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/owner/branches/${branchId}`);
      return data.branch;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Филиал не найден'));
    }
  },
);

export const createBranch = createAsyncThunk(
  'branches/create',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/owner/branches', payload);
      return data.branch;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось создать филиал'));
    }
  },
);

export const updateBranch = createAsyncThunk(
  'branches/update',
  async ({ branchId, ...payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/owner/branches/${branchId}`, payload);
      return data.branch;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось обновить филиал'));
    }
  },
);

export const deleteBranch = createAsyncThunk(
  'branches/delete',
  async (branchId, { rejectWithValue }) => {
    try {
      await api.delete(`/owner/branches/${branchId}`);
      return branchId;
    } catch (err) {
      return rejectWithValue(errMsg(err, 'Не удалось удалить филиал'));
    }
  },
);

function upsert(state, branch) {
  state.byId[branch.id] = branch;
  const idx = state.items.findIndex((b) => b.id === branch.id);
  if (idx >= 0) state.items[idx] = branch;
  else state.items.push(branch);
}

const branchesSlice = createSlice({
  name: 'branches',
  initialState,
  reducers: {
    clearBranchSaveError(state) {
      state.saveError = null;
      state.saveStatus = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranches.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.status = 'ready';
        state.items = action.payload;
        state.byId = Object.fromEntries(action.payload.map((b) => [b.id, b]));
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload;
      })
      .addCase(fetchBranch.fulfilled, (state, action) => {
        upsert(state, action.payload);
      })
      .addCase(createBranch.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        state.saveStatus = 'ready';
        upsert(state, action.payload);
      })
      .addCase(createBranch.rejected, (state, action) => {
        state.saveStatus = 'error';
        state.saveError = action.payload;
      })
      .addCase(updateBranch.pending, (state) => {
        state.saveStatus = 'loading';
        state.saveError = null;
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.saveStatus = 'ready';
        upsert(state, action.payload);
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.saveStatus = 'error';
        state.saveError = action.payload;
      })
      .addCase(deleteBranch.fulfilled, (state, action) => {
        const id = action.payload;
        delete state.byId[id];
        state.items = state.items.filter((b) => b.id !== id);
      });
  },
});

export const { clearBranchSaveError } = branchesSlice.actions;
export default branchesSlice.reducer;

export const selectBranches = (state) => state.branches.items;
export const selectBranchesStatus = (state) => state.branches.status;
export const selectBranchById = (id) =>
  createSelector(
    (state) => state.branches.byId,
    (byId) => byId[id] ?? null,
  );
